import { Typography } from '@mui/joy';
import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ICliJob } from 'cli/types';
import { Confirmation, ExpandableList } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { parseMessageContent } from 'core/utils';
import { QuotePartNoteType } from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';
import { ICustomerQuotePart, IQuotePart, IRemoveQuotePartNotesByTypePayload } from 'quotes/types';
import { Color } from 'theme/enums';

import { mapRemoveQuotePartNotesToServer } from './mappers';
import { QuotePartRemoveNotesForm } from './QuotePartRemoveNotesForm';
import { quotePartRemoveNotesSchema } from './schema';
import { IQuotePartRemoveNotesForm } from './types';
import { getAvailableNoteTypes, getNoteTypeLabel } from './utils';

interface IProps {
  isOpen: boolean;
  quoteParts: IQuotePart[];
  level?: number;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess?: () => void;
  notificationPosition?: NotificationPosition;
}

interface IPendingRemove {
  payload: IRemoveQuotePartNotesByTypePayload;
  reset: UseFormReset<IQuotePartRemoveNotesForm>;
}

export const QuotePartRemoveNotes = ({
  isOpen = false,
  quoteParts,
  level = 0,
  onClose,
  onSubmitSuccess = null,
  notificationPosition = NotificationPosition.FORM,
}: IProps) => {
  const [pendingRemove, setPendingRemove] = useState<IPendingRemove>(null);

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { clearNotification, showErrorNotification, showSuccessNotification } =
    useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { handleCliJob } = useCliJobHandler();
  const { removeQuotePartNotesByType } = useQuotePartActions();

  const availableNoteTypes = useMemo(() => getAvailableNoteTypes(quoteParts), [quoteParts]);

  const defaultValues = useMemo(
    () => ({
      types: availableNoteTypes.reduce((acc, type) => ({ ...acc, [type]: false }), {}),
    }),
    [availableNoteTypes]
  );

  // Submitting the drawer only asks for confirmation: the payload is mapped up front so the
  // dialog names exactly the note types that will go on the wire.
  const handleOnSubmit =
    (reset: UseFormReset<IQuotePartRemoveNotesForm>) => (data: IQuotePartRemoveNotesForm) => {
      const payload = mapRemoveQuotePartNotesToServer(quoteParts, data);

      // Schema validation cannot catch either case: a selected type can be dropped by the
      // availability gate, and the selection can empty out while the drawer lingers open. The
      // backend rejects both with a generic "Invalid parameters." job failure, so stop here.
      if (!payload.noteTypes.length || !payload.quotePartIds.length) {
        showErrorNotification(
          {
            title: t('titleValidationError'),
            message: payload.quotePartIds.length
              ? t('errorNoteTypeRequired')
              : t('errorLineRequired'),
          },
          notificationPosition
        );

        return;
      }

      setPendingRemove({ payload, reset });
    };

  const handleOnConfirmRemove = () => {
    const { payload, reset } = pendingRemove;

    // Confirming closes the dialog AND the drawer before the job is enqueued: Confirmation does
    // not forward `disabled` to its confirm button, so unmounting it is the only guard against a
    // double enqueue. The job's LOADING/SUCCESS notifications live at `notificationPosition`,
    // outside the drawer, and play out after it is gone — hence no `clearNotification()` here.
    // The grid selection is deliberately NOT released yet: it is the user's only way to retry a
    // request or a job that fails, and re-selecting on a filtered list is expensive.
    closeDrawer(reset);

    handleCliJob(removeQuotePartNotesByType, payload, {
      cliJobInProgressMessage: t('messageLineNotesRemoveInProgress', {
        count: payload.quotePartIds.length,
      }),
      onCliJobFinished: (cliJob: ICliJob) => {
        // quotePartNotes are the rows as they were just before removal; only their count is
        // consumed here, so it is typed down to what is actually read.
        const { quotePartNotes = [], quoteParts: updatedQuoteParts = [] } = (cliJob.result ||
          {}) as {
          quotePartNotes: { id: number }[];
          quoteParts: ICustomerQuotePart[];
        };

        // Lines that carried no note of the selected types are skipped by the backend and
        // come back in neither array, so nothing about them needs refreshing.
        if (updatedQuoteParts.length) {
          updateRows({ update: updatedQuoteParts }, { redrawRows: true });
        } else if (quotePartNotes.length) {
          // The backend drops its refresh payload when rebuilding the rows fails, but the notes
          // are already removed by then — refetch so the grid stops rendering them.
          queryClient.invalidateQueries('customerQuoteParts');
        }

        // The grid refresh is best-effort on the backend: a successful job can return removed
        // notes with an empty quoteParts array, so the removal — not the refresh — is the signal
        // that the quote draft version changed.
        if (quotePartNotes.length) {
          onSubmitSuccess && onSubmitSuccess();
        }

        showSuccessNotification(
          {
            message: quotePartNotes.length
              ? t('messageLineNotesRemoveSuccess', { count: quotePartNotes.length })
              : t('messageLineNotesRemoveNoMatch'),
          },
          notificationPosition
        );

        // Releases the grid selection — nothing is closed here, the drawer went on confirm.
        onClose(true);
      },
      notificationPosition,
    });
  };

  const closeDrawer = (reset: UseFormReset<IQuotePartRemoveNotesForm>, closeSelection = false) => {
    // A confirmation must never outlive the drawer that raised it.
    setPendingRemove(null);
    reset();
    onClose(closeSelection);
  };

  const handleOnClose = (reset?: UseFormReset<IQuotePartRemoveNotesForm>) => {
    // Notifications are pushed at TOP, which Form's own close does not clear.
    clearNotification();
    closeDrawer(reset);
  };

  const getConfirmationMessage = () => (
    <>
      {/* parseMessageContent returns a Box; a Typography paragraph cannot legally contain it. */}
      <Typography component="div">
        {parseMessageContent(
          t('messageConfirmRemoveLineNotes', { count: pendingRemove.payload.quotePartIds.length })
        )}
      </Typography>

      <ExpandableList<QuotePartNoteType>
        items={pendingRemove.payload.noteTypes}
        itemRenderer={(type) => (
          <Typography sx={{ fontWeight: 600 }}>{getNoteTypeLabel(type)}</Typography>
        )}
        getItemKey={(type) => type}
        marker="disc"
      />
    </>
  );

  return (
    <>
      <FormProvider
        schema={quotePartRemoveNotesSchema(availableNoteTypes)}
        defaultValues={defaultValues}
        resetDefaultValues
      >
        <QuotePartRemoveNotesForm
          types={availableNoteTypes}
          isOpen={isOpen}
          level={level}
          onClose={handleOnClose}
          onSubmit={handleOnSubmit}
        />
      </FormProvider>

      {pendingRemove && (
        <Confirmation
          isOpen={true}
          title={t('titleRemoveNotes')}
          message={getConfirmationMessage()}
          confirmBtnLabel={t('buttonLabelRemove')}
          confirmBtnColor={Color.DANGER}
          onConfirm={handleOnConfirmRemove}
          // Cancelling — including a backdrop click — leaves the drawer open with its selection.
          onCancel={() => setPendingRemove(null)}
        />
      )}
    </>
  );
};
